/**
 * Sämtliche Oberflächentexte. Bewusst eine einzige Datei ohne i18n-Bibliothek:
 * die Anwendung ist einsprachig, und eine Übersetzungsschicht für genau eine
 * Sprache ist Ballast. Soll später eine zweite Sprache dazu, wird aus diesem
 * Objekt der erste Eintrag einer Map — die Aufrufstellen ändern sich nicht.
 */

export const de = {
  app: {
    name: 'Timetool',
    tagline: 'Arbeitszeitkonto',
    greeting: (name: string) => `Hallo ${name}!`,
  },

  nav: {
    menu: 'Menü',
    account: 'Konto',
    profile: 'Profil ansehen',
    today: 'Heute',
    week: 'Woche & Verlauf',
    export: 'Export & Profil',
    admin: 'Verwaltung',
    logout: 'Abmelden',
  },

  auth: {
    title: 'Anmelden',
    intro: 'Mit der dienstlichen E-Mail-Adresse anmelden.',
    email: 'E-Mail',
    password: 'Passwort',
    submit: 'Anmelden',
    forgot: 'Passwort vergessen?',
    recoverTitle: 'Passwort zurücksetzen',
    recoverIntro: 'Wir senden einen Link zum Zurücksetzen an diese Adresse.',
    recoverSubmit: 'Link senden',
    recoverSent:
      'Wenn die Adresse bekannt ist, wurde eine E-Mail verschickt. Der Link darin gilt eine Stunde.',
    linkExpired:
      'Dieser Link ist abgelaufen oder wurde schon benutzt. Links gelten nur eine Stunde — bitte einen neuen anfordern.',
    setPasswordTitle: 'Passwort festlegen',
    setPasswordSubmit: 'Passwort speichern',
    setPasswordDone: 'Passwort gespeichert. Bitte melden Sie sich jetzt an.',
    backToLogin: 'Zurück zur Anmeldung',
    invalid: 'E-Mail oder Passwort stimmt nicht.',
    inactive: 'Dieses Konto ist deaktiviert. Bitte an die Verwaltung wenden.',
    noProfile: 'Zu diesem Konto gibt es kein Mitarbeiterprofil. Bitte an die Verwaltung wenden.',
    passwordTooShort: 'Das Passwort muss mindestens 8 Zeichen haben.',
    passwordMismatch: 'Die Passwörter stimmen nicht überein.',
    passwordRepeat: 'Passwort wiederholen',
    inviteExpired:
      'Dieser Einladungslink ist abgelaufen oder wurde schon benutzt. Die Verwaltung kann einen neuen schicken — oder Sie fordern hier einen Link zum Zurücksetzen an.',
    showPassword: 'Passwort anzeigen',
    hidePassword: 'Passwort verbergen',
  },

  /** Die Einladungsmail. Geht aus der Funktion `admin` hinaus, nicht aus dem Browser. */
  inviteMail: {
    subject: (app: string, company: string) => `Ihr Zugang zu ${app} (${company})`,
    greeting: (name: string) => `Hallo ${name},`,
    body: (app: string, company: string) =>
      `Sie haben einen Zugang zu ${app}, der Zeiterfassung von ${company}. Über den Link legen Sie Ihr Passwort fest und sind danach gleich angemeldet.`,
    action: 'Passwort festlegen',
    validity: (days: number) =>
      `Der Link gilt ${days} Tage und nur einmal. Ist er abgelaufen, schickt die Verwaltung gern einen neuen.`,
    notYou: 'Falls Sie mit dieser Mail nichts anfangen können, ignorieren Sie sie einfach.',
  },

  status: {
    off: 'Nicht eingestempelt',
    on: 'Im Dienst',
    break: 'In Pause',
    done: 'Tag abgeschlossen',
    missingOut: 'Gehen fehlt',
  },

  terminal: {
    stampTitle: 'Stempeluhr',
    workToday: 'Arbeitszeit heute',
    breakToday: 'Pause heute',
    netToday: 'Netto heute',
    clockIn: 'Kommen',
    clockOut: 'Gehen',
    breakStart: 'Pause beginnen',
    breakEnd: 'Pause beenden',
    clockInAgain: 'Erneut kommen',
    logTitle: 'Buchungen heute',
    logEmpty: 'Heute noch keine Buchung.',
    logClockIn: 'Kommen',
    logClockOut: 'Gehen',
    logBreakStart: 'Pause Beginn',
    logBreakEnd: 'Pause Ende',
    running: 'läuft',
    pending: '—',
    weekVsTarget: 'Diese Woche gegen Soll',
    workWeek: 'Arbeitszeit diese Woche',
    ofTarget: (target: string) => `von ${target} Soll`,
    pctOfTarget: (pct: string) => `${pct} vom Soll`,
    remaining: (rest: string) => `noch ${rest}`,
    surplus: (over: string) => `${over} über Soll`,
    weekDiff: 'Diese Woche ±',
    balance: 'Überstundensaldo',
    hintOff: 'Mit „Kommen“ beginnt die Zeiterfassung für heute.',
    hintOn: 'Die Zeit läuft. Pausen bitte stempeln — sie werden von der Arbeitszeit abgezogen.',
    hintBreak: 'Die Pause läuft und wird nicht als Arbeitszeit gezählt.',
    hintDone: 'Der Tag ist abgeschlossen. Korrekturen laufen über die Wochenansicht.',
    hintMissingOut: 'Für diesen Tag fehlt das Gehen. Bitte über die Wochenansicht korrigieren.',
  },

  week: {
    prev: 'Vorherige Woche',
    next: 'Nächste Woche',
    current: 'Aktuelle Woche',
    title: (kw: number) => `KW ${kw}`,
    colDate: 'Datum',
    colIn: 'Kommen',
    colOut: 'Gehen',
    colBreak: 'Pause',
    colNet: 'Arbeitszeit',
    colDiff: '±',
    colAction: 'Korrektur',
    net: 'Netto',
    target: 'Soll',
    correct: 'Korrigieren',
    pending: 'In Prüfung',
    corrected: 'Korrigiert',
    open: 'offen',
    weekend: 'Wochenende',
    empty: '—',
    footnote:
      'Tage ohne Gehen sind als „offen“ markiert. Korrekturen werden zur Freigabe an die Verwaltung geschickt; die ursprüngliche Buchung bleibt bis zur Entscheidung bestehen.',
  },

  correction: {
    title: 'Korrektur beantragen',
    subtitle: 'Antrag',
    in: 'Kommen',
    out: 'Gehen',
    breakMinutes: 'Pause (Min.)',
    reason: 'Begründung',
    reasonPlaceholder: 'z. B. Ausstempeln vergessen',
    net: 'Arbeitszeit',
    submit: 'Antrag senden',
    cancel: 'Abbrechen',
    reasonRequired: 'Bitte eine Begründung angeben.',
    breakTooLong: 'Die Pause ist länger als die Anwesenheit.',
    outBeforeIn: 'Gehen liegt vor Kommen.',
    noChange: 'Die Angaben entsprechen der bestehenden Buchung.',
    submitted: 'Antrag gesendet',
    previous: 'Bisher',
    proposed: 'Beantragt',
  },

  export: {
    title: 'Daten exportieren',
    period: 'Zeitraum',
    periodWeek: 'Diese Woche',
    periodMonth: 'Dieser Monat',
    scope: 'Umfang',
    scopeAll: 'Alle Mitarbeiter',
    csv: 'CSV herunterladen',
    print: 'Druckansicht',
    summary: (rows: number, period: string) => `${rows} Buchungen im Zeitraum ${period}.`,
    printTitle: (period: string) => `Arbeitszeitnachweis — ${period}`,
    generated: (when: string) => `Erstellt am ${when}`,
    totalNet: 'Summe Arbeitszeit',
    weekTarget: 'Wochensoll',
    difference: 'Abweichung',
    balanceCarried: 'Überstundensaldo',
    noRecords: 'Keine Buchungen in diesem Zeitraum.',
    csvHeaders: [
      'Mitarbeiter', 'Datum', 'Wochentag', 'Kommen', 'Gehen',
      'Pause', 'Arbeitszeit', 'Arbeitszeit (Std.)', 'Status', 'Hinweis',
    ],
    popupBlocked: 'Bitte Pop-ups erlauben, um die Druckansicht zu öffnen.',
  },

  profile: {
    title: 'Profil',
    weeklyTarget: 'Wochensoll (Stunden)',
    workdays: 'Arbeitstage pro Woche',
    openingBalance: 'Anfangssaldo (Stunden)',
    employmentStart: 'Eintritt',
    department: 'Bereich',
    readonlyHint:
      'Wochensoll und Anfangssaldo sind vertraglich vereinbart und werden von der Verwaltung gepflegt.',
    retentionHint:
      'Buchungen werden nach § 16 Abs. 2 ArbZG zwei Jahre aufbewahrt und nicht gelöscht.',
  },

  reminder: {
    title: 'Morgenerinnerung',
    intro:
      'Wenn Sie zur üblichen Zeit noch nicht eingestempelt haben, kommt eine Benachrichtigung auf dieses Gerät. Sie erinnert nur — gestempelt wird nichts.',
    on: 'An',
    off: 'Aus',
    enable: 'Auf diesem Gerät aktivieren',
    disable: 'Auf diesem Gerät abschalten',
    test: 'Probe senden',
    testSent: 'Probe gesendet',
    enabled: 'Erinnerung aktiviert',
    disabled: 'Erinnerung abgeschaltet',
    otherDevices: (n: number) =>
      n === 1 ? 'Auf einem anderen Gerät aktiv.' : `Auf ${n} anderen Geräten aktiv.`,
    next: (day: string, time: string) => `Nächste Erinnerung: ${day} gegen ${time} Uhr.`,
    today: 'heute',
    tomorrow: 'morgen',
    noNext:
      'Noch keine übliche Anfangszeit bekannt — sie ergibt sich nach ein paar Arbeitstagen. Bis dahin eine feste Uhrzeit eintragen.',
    time: 'Feste Uhrzeit (optional)',
    timeHint: (detected: string | null) =>
      detected
        ? `Leer lassen, um die übliche Zeit zu nehmen — zurzeit ${detected} Uhr. Erinnert wird eine Viertelstunde danach.`
        : 'Leer lassen, um die übliche Zeit aus den letzten Wochen zu nehmen. Erinnert wird eine Viertelstunde danach.',
    pause: 'Keine Erinnerung bis einschliesslich',
    pauseToday: 'Heute nicht erinnern',
    pauseClear: 'Pause aufheben',
    paused: (day: string) => `Pausiert bis einschliesslich ${day}.`,
    save: 'Speichern',
    unsupported: 'Dieser Browser kann keine Benachrichtigungen empfangen.',
    iosInstall:
      'Auf dem iPhone kommen Benachrichtigungen nur, wenn Timetool auf dem Home-Bildschirm liegt: unten auf „Teilen“ tippen, dann „Zum Home-Bildschirm“, und Timetool von dort öffnen.',
    denied:
      'Benachrichtigungen sind für Timetool im Browser gesperrt. Sie lassen sich in den Website-Einstellungen wieder erlauben.',
    notificationTitle: 'Noch nicht eingestempelt',
    notificationBody: (time: string) =>
      `Guten Morgen! Sie fangen meist gegen ${time} Uhr an — bitte „Kommen“ buchen, falls Sie schon da sind.`,
    testTitle: 'Timetool',
    testBody: 'So sieht die Morgenerinnerung aus.',
  },

  admin: {
    subtitle: 'Anträge, Mitarbeiter, Protokoll',
    title: 'Verwaltung',
    tabEmployees: 'Mitarbeiter',
    tabRequests: 'Korrekturanträge',
    tabAudit: 'Protokoll',
    inviteTitle: 'Mitarbeiter einladen',
    inviteName: 'Vollständiger Name',
    inviteEmail: 'E-Mail',
    inviteDepartment: 'Bereich',
    inviteSubmit: 'Einladen',
    inviteSent: 'Einladung verschickt',
    inviteMailFailed:
      'Konto angelegt, aber die Einladungsmail ging nicht hinaus. Bitte über „Zugangslink senden“ erneut versuchen.',
    sendAccess: 'Zugangslink senden',
    inviteValidity: (days: number) => `Der Link in der Mail gilt ${days} Tage.`,
    accessSent: 'Zugangslink verschickt',
    edit: 'Bearbeiten',
    editTitle: 'Mitarbeiter bearbeiten',
    editHint:
      'Eintritt und Anfangssaldo wirken rückwirkend: der Überstundensaldo wird daraus neu gerechnet.',
    editNoChange: 'Nichts geändert.',
    employeeActive: 'Aktiv',
    employeeInactive: 'Deaktiviert',
    deactivate: 'Deaktivieren',
    reactivate: 'Reaktivieren',
    save: 'Speichern',
    requestsEmpty: 'Keine offenen Anträge.',
    approve: 'Freigeben',
    reject: 'Ablehnen',
    decisionNote: 'Anmerkung (optional)',
    approved: 'Freigegeben',
    rejected: 'Abgelehnt',
    auditEmpty: 'Noch keine Einträge.',
    colWhen: 'Zeitpunkt',
    colWho: 'Wer',
    colWhat: 'Aktion',
    colDay: 'Betroffener Tag',
  },

  toast: {
    clock_in: 'Kommen gebucht',
    clock_out: 'Gehen gebucht',
    break_start: 'Pause begonnen',
    break_end: 'Pause beendet',
    correction_requested: 'Korrekturantrag gesendet',
    correction_approved: 'Korrektur freigegeben',
    correction_rejected: 'Antrag abgelehnt',
    saved: 'Gespeichert',
    csv: 'CSV heruntergeladen',
  },

  error: {
    generic: 'Das hat nicht geklappt. Bitte erneut versuchen.',
    offline: 'Keine Verbindung zum Server.',
    conflict: 'Die Buchung passt nicht zum aktuellen Stand. Die Seite wird neu geladen.',
    forbidden: 'Dafür fehlt die Berechtigung.',
  },

  audit: {
    clock_in: 'Kommen',
    clock_out: 'Gehen',
    break_start: 'Pause Beginn',
    break_end: 'Pause Ende',
    correction_requested: 'Korrektur beantragt',
    correction_approved: 'Korrektur freigegeben',
    correction_rejected: 'Korrektur abgelehnt',
    employee_invited: 'Mitarbeiter eingeladen',
    employee_updated: 'Profil geändert',
    employee_deactivated: 'Mitarbeiter deaktiviert',
    day_auto_closed: 'Tag automatisch abgeschlossen',
  },
} as const;

export type Strings = typeof de;
